import {
    PublicVibrixEvent,
    VibrixEvent,
    VibrixEventMailTemplate,
} from "@/interfaces/vibrixEvent";
import { VibrixEventRegistration } from "@/interfaces/vibrixEventRegistration";
import axios from "axios";

export class EventService {
    public static async createEvent(
        event: {
            studio_id: string;
            title: string;
            description: string;
            join_description: string;
            start_time: string;
            end_time: string;
            is_private: boolean;
            invitationList: { email: string }[];
            blockList: { email: string }[];
            mailTemplates: VibrixEventMailTemplate;
        },
        token: string
    ): Promise<VibrixEvent> {
        const uri = "/api/events";
        const response = await axios.post(uri, event, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }

    public static async updateEvent(
        event: {
            id: string;
            title: string;
            description: string;
            join_description: string;
            is_private: boolean;
            invitationList: { email: string }[];
            blockList: { email: string }[];
            mailTemplates: VibrixEventMailTemplate;
            maxParticipants: number;
            isFullDescription: string;
        },
        token: string
    ): Promise<VibrixEvent> {
        const uri = "/api/events/";
        const response = await axios.put(uri, event, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }

    public static async createRegistration(registration: {
        event_id: string;
        signupMeta: Record<string, string | boolean>;
    }): Promise<VibrixEventRegistration> {
        const uri = "/api/events/registration";
        return await axios
            .post(uri, registration)
            .then(r => {
                return r.data;
            })
            .catch(err => {
                throw new Error(err.response.data.detail);
            });
    }

    public static async updateRegistration(id: string): Promise<number> {
        const uri = "/api/events/registration";
        const response = await axios.patch(uri, { id });
        return response.data;
    }

    public static async deleteEvent(
        eventId: string,
        token: string
    ): Promise<boolean> {
        const uri = `/api/events?id=${eventId}`;
        const response = await axios.delete(uri, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.status === 200;
    }

    public static async getPublicEvents(
        ownerId: string
    ): Promise<PublicVibrixEvent[]> {
        const uri = `/api/events/public?ownerId=${ownerId}`;
        const response = await axios.get(uri);
        return response.data;
    }

    public static async getEventByIdPublic(
        eventId: string
    ): Promise<PublicVibrixEvent> {
        const uri = `/api/events/by_id_public/${eventId}`;
        const response = await axios.get(uri);
        return response.data;
    }

    public static async getIsRegistrationValid(
        eventId: string,
        email: string
    ): Promise<boolean> {
        const uri = `/api/events/registration/isvalid/${eventId}/${email}`;
        const response = await axios.get(uri);
        return response.data;
    }

    public static async getEventRegistration(
        registrationId: string
    ): Promise<VibrixEventRegistration> {
        const uri = `/api/events/registration/by_id/${registrationId}`;
        const response = await axios.get(uri);
        return response.data;
    }

    public static async getAllEvents(token: string): Promise<VibrixEvent[]> {
        const uri = `/api/events/all`;
        const response = await axios.get(uri, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }

    public static async getEventById(
        eventId: string,
        token: string
    ): Promise<VibrixEvent> {
        const uri = `/api/events/by_id/eventId=?${eventId}`;
        const response = await axios.get(uri, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }

    public static async getICSbyRegistration(registrationId: string) {
        const uri = `/api/events/ics?registrationId=${registrationId}`;
        const response = await axios.get(uri);
        return response;
    }

    public static async parseCsv(
        f: File,
        token: string
    ): Promise<{ email: string }[]> {
        const uri = "/api/events/parseCSV";
        const formdata = new FormData();
        formdata.append("file", f);
        const response = await axios.post(uri, formdata, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return response.data;
    }
}
